/**
 * Bubble View Physics Module
 * Runs the layout simulation (constraints in layout.js) and position updates
 */

import { setBubblePositionsBatch } from "../../store/bubble-positions.js";
import * as LabelLOD from "./label-lod.js";
import * as Titles from "./titles.js";
import { enforceConstraints, maxCenterDistance, maxReach, residualOverlaps, separation, SIBLING_GAP } from "./layout.js";

// Speed: simulation steps per animation frame (within FRAME_BUDGET_MS). The
// motion itself is unchanged; 16 plays it sixteen times as fast as one step
// per frame. Big maps may get fewer steps per frame (the budget), so move
// slower.
const STEPS_PER_FRAME = 16;
// Speed limit (px per frame). Only the first push apart, where bubbles
// overlap a lot, gets this fast; the slower settling motion stays below it.
const MAX_SPEED = 20;
const FRAME_BUDGET_MS = 10;
// Every settle animation lasts this long: the centering pull acts at full
// strength for the first HOLD part of it, then fades out smoothly to rest
// (without the fade, pull and push never balanced: bubbles crept for
// seconds and rested overlapping where many pressed together).
const DURATION_MS = 4000;
const HOLD = 0.5;
// Above this many bubbles, drawing a frame costs more than the simulation
// (27,000 bubbles: about 0.3 s of style work per frame, so the 4 s
// animation got about ten steps). On such a map the groups nobody can see
// (inside a bubble too small, or off screen: label-lod.js) are computed
// first without drawing: QUIET_STEPS steps in slices of SLICE_MS between
// frames. The groups on screen then move as on a small map, when at most
// this many of their bubbles move (Alex, 2026-10-15); otherwise they are
// computed the same way, and the map is drawn once.
const ANIMATE_MAX_NODES = 3000;
// When at most this many bubbles move (a drag release, an added item), each
// gets its own compositor layer for the motion (CSS .bubble.moving): it moves
// without repainting the thousands of bubbles around it. Never more, and
// never for good: one layer per bubble once took about 2 GB of GPU memory
// (715 bubbles, main.css .bubble note).
const LAYERED_MAX_NODES = 200;
const QUIET_STEPS = 60;
const SLICE_MS = 30;
// Overlapping shapes push each other apart through their velocities with
// this stiffness (per px of overlap); placed bubbles yield less.
const SPRING = 0.5;
const PLACED_FACTOR = 0.3;
const DAMPING = 0.75;
// Constraint passes at rest, before saving: removes what is left of any
// overlap (the motion leaves at most a few px).
const SETTLE_PASSES = 30;
// ... and up to this many while overlaps are left: a large map's simulation
// runs few steps (each frame moves thousands of bubbles) and ended with
// overlapping siblings, so every later opening simulated again.
const MAX_SETTLE_PASSES = 200;

// ============================================================================
// PHYSICS STATE
// ============================================================================

let physicsNodes = [];
let animationId = null;
// What the last run did (checks): nodes, animated, and on a large map how
// many were seen and moving.
let lastRun = null;
// Makes the test of what can be seen with the view as it is now (index.js):
// every run, whoever starts it (a render, a drag release), animates what can
// be seen. A drag release had no test, so on a large map it was never
// animated (Alex, 2026-10-15: "super choppy").
let seenTestMaker = null;

export function setSeenTest(maker) {
  seenTestMaker = maker;
}
let isPhysicsRunning = false;
let renderPendingAfterPhysics = false;

// ============================================================================
// STATE ACCESSORS
// ============================================================================

export function getLastRun() {
  return lastRun;
}

export function getPhysicsNodes() {
  return physicsNodes;
}

export function setPhysicsNodes(nodes) {
  physicsNodes = nodes;
}

export function isRunning() {
  return isPhysicsRunning;
}

export function isRenderPending() {
  return renderPendingAfterPhysics;
}

export function setRenderPending(pending) {
  renderPendingAfterPhysics = pending;
}

// ============================================================================
// PHYSICS CONTROL
// ============================================================================

/**
 * Cancel any running physics simulation
 */
export function cancelPhysics() {
  if (animationId) {
    cancelAnimationFrame(animationId);
    animationId = null;
  }
  dropLayers();
  isPhysicsRunning = false;
  renderPendingAfterPhysics = false;
}

/**
 * Start physics simulation
 * @param {Object} options - Configuration options
 * @param {Function} options.onFinish - Callback when physics completes
 * @param {Function} options.scheduleRender - Function to schedule a render
 * @param {string} options.currentDocId - Current document ID for saving positions
 * @param {Array<string>} [options.dirtyIds] - Ids of the nodes that actually
 *   changed (new node, changed radius, drag release). When given, the
 *   simulation is SCOPED: only the dirty nodes plus the sibling groups
 *   along their ancestor chains may move — everything else is frozen in
 *   place but still collides. Without this, a single added child
 *   re-equilibrated the WHOLE map and the settled layout visibly
 *   contracted toward the center ("all bubbles shrink").
 */
let layered = []; // bubbles with their own layer for the running motion

/** The motion is over (settled, or cancelled): its bubbles lose their layer. */
function dropLayers() {
  LabelLOD.setMotion(false);
  for (const n of layered) n.el.classList.remove("moving");
  layered = [];
}

export function startPhysics(options = {}) {
  const { onFinish, scheduleRender, currentDocId, dirtyIds } = options;
  const isSeen = options.isSeen ?? seenTestMaker?.();

  if (animationId) cancelAnimationFrame(animationId);
  dropLayers();

  // Mark physics as running to prevent mid-simulation renders
  isPhysicsRunning = true;

  // Build parent-child relationships
  const topLevelNodes = [];
  physicsNodes.forEach((n) => {
    n.children = [];
    if (!n.parent) topLevelNodes.push(n);
  });
  physicsNodes.forEach((n) => {
    if (n.parent) n.parent.children.push(n);
  });

  // Expand dirtyIds into the movable set: each dirty node's sibling group
  // (they must shuffle aside), repeated up the ancestor chain (a grown
  // parent pushes ITS siblings too). If more than half the map would move
  // anyway (first layout, fold-all), run unscoped — freezing buys nothing.
  let activeIds = null;
  if (Array.isArray(dirtyIds) && dirtyIds.length) {
    const byId = new Map(physicsNodes.map((n) => [n.id, n]));
    const active = new Set();
    for (const id of dirtyIds) {
      let n = byId.get(id);
      while (n) {
        const group = n.parent ? n.parent.children : topLevelNodes;
        for (const s of group) active.add(s.id);
        n = n.parent;
      }
    }
    if (active.size <= physicsNodes.length * 0.5) {
      activeIds = active;
    }
  }

  // Sibling groups. Frozen nodes (scoped run) don't yield, so active nodes
  // are pushed off them instead.
  const groups = [{ parent: null, nodes: topLevelNodes }];
  for (const n of physicsNodes) if (n.children.length) groups.push({ parent: n, nodes: n.children });
  // Fixed for the run: computed once (called for every node at every step).
  for (const n of physicsNodes) n.weight = activeIds && !activeIds.has(n.id) ? 0 : n.manuallyPositioned ? PLACED_FACTOR : 1;
  const weight = (n) => n.weight;

  // 1 during HOLD, then a smooth fade to 0 at the end of the run (p: 0 → 1).
  const heatOf = (p) => {
    if (p <= HOLD) return 1;
    if (p >= 1) return 0;
    return 0.5 * (1 + Math.cos((Math.PI * (p - HOLD)) / (1 - HOLD)));
  };

  // Resolve what is left of the overlaps, draw, and save the layout.
  function settle() {
    dropLayers();
    for (let pass = 1; pass <= MAX_SETTLE_PASSES; pass++) {
      enforceConstraints(groups, weight);
      if (pass >= SETTLE_PASSES && pass % 10 === 0 && !residualOverlaps(physicsNodes).size) break;
    }
    for (let i = 0; i < physicsNodes.length; i++) updateBubblePos(physicsNodes[i]);
    Titles.drawNow();
    finishPhysics(currentDocId, onFinish, scheduleRender);
  }

  // The motion, drawn: stepNodes (those of stepGroups) move and are written
  // each frame; the others keep their place in their parent. Few movers get
  // a layer each; many, on a large map, move without the dots meanwhile
  // (label-lod.js setMotion). 20,000 items, 2x screen: a drag release (24
  // movers) 17 ms a frame either way; a first opening (558) 100 ms a frame
  // with the dots, 17 without.
  function animate(stepGroups, stepNodes) {
    const movers = activeIds ? stepNodes.filter((n) => activeIds.has(n.id)) : stepNodes;
    if (movers.length > LAYERED_MAX_NODES && physicsNodes.length > ANIMATE_MAX_NODES) LabelLOD.setMotion(true);
    if (movers.length <= LAYERED_MAX_NODES) {
      layered = movers.filter((n) => n.el);
      for (const n of layered) n.el.classList.add("moving");
    }
    const startTime = performance.now();
    function step() {
      const frameStart = performance.now();
      const heat = heatOf((frameStart - startTime) / DURATION_MS);
      for (let i = 0; i < STEPS_PER_FRAME; i++) {
        simulateStep(stepGroups, weight, activeIds, heat, stepNodes);
        if (performance.now() - frameStart > FRAME_BUDGET_MS) break;
      }

      // One DOM write per frame, whatever the number of steps. Frozen nodes'
      // labels still move when a moving ancestor carries them.
      for (let i = 0; i < stepNodes.length; i++) updateBubblePos(stepNodes[i]);

      if (heat > 0) {
        animationId = requestAnimationFrame(step);
        // The titles with them, in this frame; after the next step is
        // requested, so that step's own draw replaces any redraw the titles
        // ask for meanwhile (titles.js drawNow).
        Titles.drawNow();
      } else settle();
    }
    step();
  }

  if (physicsNodes.length <= ANIMATE_MAX_NODES) {
    lastRun = { nodes: physicsNodes.length, animated: true };
    animate(groups, physicsNodes);
    return;
  }

  // A large map: the groups not seen are computed without drawing (their
  // bubbles keep their place in their parent while it moves), then the
  // groups seen move, when few enough of their bubbles do.
  const seenGroups = [];
  const unseenGroups = [];
  for (const g of groups) (isSeen && g.nodes.some(isSeen) ? seenGroups : unseenGroups).push(g);
  const seenNodes = seenGroups.flatMap((g) => g.nodes);
  const moving = activeIds ? seenNodes.filter((n) => activeIds.has(n.id)).length : seenNodes.length;
  const drawn = moving > 0 && moving <= ANIMATE_MAX_NODES;
  lastRun = { nodes: physicsNodes.length, animated: drawn, seen: seenNodes.length, moving };
  const quietGroups = drawn ? unseenGroups : groups;
  const quietNodes = drawn ? unseenGroups.flatMap((g) => g.nodes) : physicsNodes;
  let done = quietNodes.length ? 0 : QUIET_STEPS;
  const slice = () => {
    const sliceStart = performance.now();
    while (done < QUIET_STEPS && performance.now() - sliceStart < SLICE_MS) {
      simulateStep(quietGroups, weight, activeIds, heatOf(done / QUIET_STEPS), quietNodes);
      done++;
    }
    if (done < QUIET_STEPS) animationId = requestAnimationFrame(slice);
    else if (drawn) animate(seenGroups, seenNodes);
    else settle();
  };
  slice();
}

/**
 * One simulation step: overlapping siblings push each other apart and the
 * centering pull draws nodes in (both through velocities, so bubbles glide),
 * then children are kept inside their parent.
 * @param {number} heat - 1 → 0 over the run, scales the centering pull
 * @param {Array} [nodes] - the nodes that move (those of groups); default all
 */
function simulateStep(groups, weight, activeIds, heat, nodes = physicsNodes) {
  for (const group of groups) applySeparationForces(group.nodes);

  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];

    // Scoped simulation: frozen nodes never move (velocities cleared so
    // accumulated forces can't build up), but they still take part in the
    // separation above, so active nodes collide with them.
    if (activeIds && !activeIds.has(n.id)) {
      n.vx = 0;
      n.vy = 0;
      continue;
    }

    // Centering pulls toward the (parent's) origin with force ∝ distance.
    // For PLACED top-level nodes that is a collapse vector: on a large map
    // the inward pressure of every outer ring accumulates and the
    // "equilibrium" is a pile of deeply overlapping circles (measured: one
    // re-simulation crushed a 9400px-wide layout to 4400px of concentric
    // rings). Placed top-level nodes therefore get essentially NO
    // centering — re-running the sim can only push overlaps apart, never
    // compact a good layout. Fresh nodes keep strong centering so they fly
    // toward their spot; placed nested nodes keep a mild pull (they're
    // hard-clamped inside their parent anyway, so distance is bounded).
    const centeringForce = n.manuallyPositioned
      ? n.parent
        ? 0.005
        : 0.0002
      : n.parent
        ? 0.03
        : 0.01;

    n.vx -= n.x * centeringForce * heat;
    n.vy -= n.y * centeringForce * heat;
    const speed = Math.hypot(n.vx, n.vy);
    if (speed > MAX_SPEED / STEPS_PER_FRAME) {
      const scale = MAX_SPEED / STEPS_PER_FRAME / speed;
      n.vx *= scale;
      n.vy *= scale;
    }

    n.x += n.vx;
    n.y += n.vy;
    n.vx *= DAMPING;
    n.vy *= DAMPING;

    // Keep children inside their parent, titles included (layout.js).
    if (n.parent) {
      const dist = Math.hypot(n.x, n.y);
      const max = dist > 1e-9 ? maxCenterDistance(n, n.y / dist, n.parent) : Infinity;
      if (dist > max) {
        const scale = max / dist;
        n.x *= scale;
        n.y *= scale;
        n.vx *= 0.5;
        n.vy *= 0.5;
      }
    }
    // Don't save to dataNode - positions are stored in localStorage only
    // This prevents bubble positions from being synced via CRDT
  }
}

/**
 * Push overlapping siblings apart through their velocities, in proportion
 * to the overlap of their whole shapes (titles and gap included).
 * @param {Array} nodes - Physics nodes at the same level
 */
function applySeparationForces(nodes) {
  const len = nodes.length;
  for (let i = 0; i < len; i++) {
    const a = nodes[i];
    const aMax = maxReach(a);
    for (let j = i + 1; j < len; j++) {
      const b = nodes[j];
      let dx = b.x - a.x;
      let dy = b.y - a.y;
      const far = aMax + maxReach(b) + SIBLING_GAP;
      let distSq = dx * dx + dy * dy;
      if (distSq >= far * far) continue;
      // Reparenting can put siblings at the same center. Give coincident pairs
      // a deterministic separation direction instead of leaving them stacked.
      if (distSq < 1e-8) {
        const angle = (i * 31 + j) * 2.399963229728653;
        dx = Math.cos(angle); dy = Math.sin(angle); distSq = 1;
      }
      const dist = Math.sqrt(distSq);
      const ux = dx / dist;
      const uy = dy / dist;
      const overlap = separation(a, b, uy) - dist;
      if (overlap <= 0) continue;
      const force = overlap * SPRING;
      const wa = a.manuallyPositioned ? PLACED_FACTOR : 1;
      const wb = b.manuallyPositioned ? PLACED_FACTOR : 1;
      a.vx -= ux * force * wa;
      a.vy -= uy * force * wa;
      b.vx += ux * force * wb;
      b.vy += uy * force * wb;
    }
  }
}

/**
 * Called when physics simulation completes
 * Handles pending renders that were deferred during physics
 * @param {string} currentDocId - Current document ID
 * @param {Function} onFinish - Optional callback
 * @param {Function} scheduleRender - Function to schedule a render
 */
function finishPhysics(currentDocId, onFinish, scheduleRender) {
  // Save all positions to localStorage
  saveAllPositions(currentDocId);

  // Labels moved during the simulation — update the LOD culling cache so
  // visibility matches the settled layout.
  LabelLOD.refreshPositions();

  // Mark physics as complete
  isPhysicsRunning = false;
  animationId = null;

  // Call optional finish callback
  if (onFinish) {
    onFinish();
  }

  // If a render was requested during physics, do it now
  if (renderPendingAfterPhysics) {
    renderPendingAfterPhysics = false;
    if (scheduleRender) {
      scheduleRender();
    }
  }
}

// ============================================================================
// POSITION UPDATES
// ============================================================================

/**
 * Update bubble DOM position
 * @param {Object} n - Physics node
 */
export function updateBubblePos(n) {
  // Canvas mode: physics nodes carry no DOM (el null) — the canvas renderer
  // reads x/y directly at paint time, nothing to write here. Titles are drawn
  // from x/y too (titles.js).
  if (!n.el) return;

  const x = n.offsetX + n.x - n.radius;
  const y = n.offsetY + n.y - n.radius;

  // Only touch the DOM when the position actually moved (>0.3px). With
  // hundreds of bubbles, unconditionally rewriting every transform each
  // physics frame forced a full style-recalc pass per frame — profiled at
  // ~10s of main-thread saturation on a 715-node map. Near convergence
  // almost nothing moves, so gating writes collapses that cost. The bubble
  // divs are nested, so this transform is parent-relative and independent
  // of ancestor motion.
  if (n._bx === undefined || Math.abs(x - n._bx) > 0.3 || Math.abs(y - n._by) > 0.3) {
    n._bx = x;
    n._by = y;
    n.el.style.transform = `translate(${x}px, ${y}px)`;
  }
}

/**
 * Save all bubble positions after physics converges (interfaces/classic/store/bubble-positions.js)
 * @param {string} currentDocId - Current document ID
 */
export function saveAllPositions(currentDocId) {
  if (!currentDocId || !physicsNodes.length) return;
  // What a node still overlaps a sibling by (a crowded parent the layout
  // cannot resolve) is saved with it: the next render takes that overlap as
  // settled instead of simulating again (render.js collectDirtyPhysicsIds).
  const left = residualOverlaps(physicsNodes);

  // Radius and actual tree parent identify the coordinate frame/equilibrium.
  // Reparenting or resizing invalidates the stamp on the next render.
  // A render can be replacing another document's physics nodes. Never copy
  // their cache entries into the newly opened document.
  const positions = physicsNodes.filter(n => n.docId === currentDocId)
    .map((n) => ({ id: n.id, x: n.x, y: n.y, r: n.radius, parentId: n.parentId, o: left.get(n) }));
  setBubblePositionsBatch(currentDocId, positions);
}


// ============================================================================
// CLEANUP
// ============================================================================

/**
 * Reset physics state
 */
export function resetPhysicsState() {
  cancelPhysics();
  physicsNodes = [];
}
